import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';

function optInCreatedAtMs(data) {
  const c = data?.createdAt;
  const d = c?.toDate?.() ?? (c instanceof Date ? c : null);
  return d && typeof d.getTime === 'function' ? d.getTime() : 0;
}

/**
 * Asc bounty sort key; missing / invalid bounty sorts last (after largest amounts).
 */
function optInBountySortKey(data) {
  const raw = data?.bountyAmount;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : Number.POSITIVE_INFINITY;
}

/**
 * Map sharedPosters doc + optional dinner opt-in row into the same ad shape as wantedFeed tiles.
 *
 * @param {string} posterId — sharedPosters doc id (= saved collage id)
 * @param {Record<string, unknown>} data — sharedPosters document data
 * @param {{ displayName?: string|null, posterNickname?: string|null, createdAt?: import('@firebase/firestore').Timestamp|Date|null, bountyAmount?: number|null }} [optIn]
 */
export function mapSharedPosterToFeedAd(posterId, data, optIn = null) {
  const d = data && typeof data === 'object' ? data : {};
  const createdFromOptIn = optIn?.createdAt?.toDate?.() ?? (optIn?.createdAt instanceof Date ? optIn.createdAt : null);
  const createdFromPoster = d.createdAt?.toDate?.() ?? (d.createdAt instanceof Date ? d.createdAt : new Date(0));
  const userName =
    (typeof d.userName === 'string' && d.userName.trim()) ||
    (typeof optIn?.displayName === 'string' && optIn.displayName.trim()) ||
    'Someone';
  // CAL LAUNCH SPECIAL: FIVE PRIZES — admin sets bountyAmount on dinnerGiveawayOptIns alongside postedInFeed.
  // The prized collage's display is the collage's existing posterNickname; no separate label field.
  const bountyAmountRaw = optIn?.bountyAmount;
  const bountyAmount = typeof bountyAmountRaw === 'number' && Number.isFinite(bountyAmountRaw) ? bountyAmountRaw : null;
  return {
    id: posterId,
    feedSource: 'dinnerGiveaway',
    partnerCutout: d.partnerCutout ?? null,
    heartPhotoUrl: d.heartPhotoUrl ?? null,
    userPhotoUrl: d.userPhotoUrl ?? null,
    swags: Array.isArray(d.swags) ? d.swags : [],
    places: Array.isArray(d.places) ? d.places : [],
    activityX: d.activityX ?? '',
    activityY: d.activityY ?? '',
    activityZ: d.activityZ ?? null,
    userId: d.userId ?? null,
    userName,
    posterNickname: (typeof optIn?.posterNickname === 'string' && optIn.posterNickname.trim())
      ? optIn.posterNickname.trim()
      : (d.posterNickname ?? 'The One'),
    dominantPersonality: d.dominantPersonality ?? null,
    alsoPrivateMatch: typeof d.isPrivateCollage === 'boolean' ? d.isPrivateCollage : undefined,
    friendLuv: typeof d.friendLuv === 'boolean' ? d.friendLuv : undefined,
    romanticLuv: typeof d.romanticLuv === 'boolean' ? d.romanticLuv : undefined,
    selfGender:
      d.selfGender && ['male', 'female', 'nb'].includes(d.selfGender) ? d.selfGender : undefined,
    selfAge:
      typeof d.selfAge === 'number' && Number.isFinite(d.selfAge) ? d.selfAge : undefined,
    lookingForGenders: (() => {
      const lf = d.lookingForGenders;
      if (!Array.isArray(lf) || lf.length === 0) return undefined;
      const allowed = lf
        .map((g) => (typeof g === 'string' ? g.trim().toLowerCase() : ''))
        .filter((g) => g === 'male' || g === 'female' || g === 'nb');
      return allowed.length ? [...new Set(allowed)] : undefined;
    })(),
    createdAt: createdFromOptIn && createdFromOptIn.getTime?.() > 0 ? createdFromOptIn : createdFromPoster,
    friendCollageShare: typeof d.friendCollageShare === 'boolean' ? d.friendCollageShare : undefined,
    friendFoxMaskPlaceholder:
      typeof d.friendFoxMaskPlaceholder === 'boolean' ? d.friendFoxMaskPlaceholder : undefined,
    surveyAnswers: d.surveyAnswers ?? null,
    bountyAmount,
    // Permanent vibe aura (mirrored onto sharedPosters so public feed/share links can render it).
    auraColor: (d.auraColor && typeof d.auraColor === 'object') ? d.auraColor : undefined,
  };
}

/**
 * When `sharedPosters` lacks `auraColor`, try `userPublicPrimary` (auth required by rules).
 * No-op when aura is already present or the read fails (e.g. signed-out homepage).
 * Returns the same `ad` reference when nothing changed, so callers can cheap-diff.
 */
export async function attachAuraColorFromPublicPrimary(firestore, ad) {
  if (!ad?.userId || (ad.auraColor && typeof ad.auraColor === 'object')) return ad;
  try {
    const pub = await getDoc(doc(firestore, 'userPublicPrimary', ad.userId));
    if (!pub.exists()) return ad;
    const aura = pub.data()?.ad?.auraColor;
    if (aura && typeof aura === 'object') return { ...ad, auraColor: aura };
  } catch (_) {
    /* signed-out / rules — leave ad unchanged */
  }
  return ad;
}

/** Cash string for MATCH A FRIEND / share prompts when the ad is a FIVE BOUNTIES dinner row. */
export function matchFriendPromptBountyDollars(ad) {
  if (!ad || ad.feedSource !== 'dinnerGiveaway') return null;
  const n = ad.bountyAmount;
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  return `$${n}`;
}

/**
 * Load one WEEKLY FEATURED carousel collage for a regional admin flag on `dinnerGiveawayOptIns`.
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @param {'berkeleyfeature'|'sfmarinafeature'|'sfnorthbeachfeature'|'sfsomafeature'|'sfdowntownfeature'} featureField
 * @param {string} logLabel
 * @returns {Promise<Array<ReturnType<typeof mapSharedPosterToFeedAd>>>}
 */
async function fetchRegionalFeaturedFeedAds(firestore, featureField, logLabel) {
  const q = query(
    collection(firestore, 'dinnerGiveawayOptIns'),
    where(featureField, '==', true),
    limit(25),
  );
  let snap;
  try {
    snap = await getDocs(q);
  } catch (err) {
    console.error(
      `dinnerGiveawayOptIns ${logLabel} query failed (deploy firestore rules; use boolean true for ${featureField}):`,
      err,
    );
    return [];
  }

  const sortedDocs = snap.docs
    .slice()
    .sort((a, b) => optInCreatedAtMs(b.data()) - optInCreatedAtMs(a.data()))
    .slice(0, 1);

  const out = [];
  for (const docSnap of sortedDocs) {
    const o = docSnap.data();
    const rawPid = o.posterId;
    const posterId = typeof rawPid === 'string' ? rawPid.trim() : String(rawPid ?? '').trim();
    if (!posterId) {
      console.warn(
        `${logLabel} feed: opt-in doc has no posterId (must match sharedPosters doc id). optIn doc id:`,
        docSnap.id,
      );
      continue;
    }
    try {
      const ps = await getDoc(doc(firestore, 'sharedPosters', posterId));
      if (!ps.exists()) {
        console.warn(
          `${logLabel} feed: no sharedPosters for posterId (save collage first so /sharedPosters/{id} exists). posterId=`,
          posterId,
          'optIn doc=',
          docSnap.id,
        );
        continue;
      }
      const ad = mapSharedPosterToFeedAd(ps.id, ps.data(), o);
      out.push(await attachAuraColorFromPublicPrimary(firestore, ad));
    } catch (e) {
      console.error(`sharedPosters fetch for ${logLabel} feed:`, posterId, e);
    }
  }
  return out;
}

/** Berkeley WEEKLY FEATURED carousel (`berkeleyfeature === true`). */
export function fetchBerkeleyFeaturedFeedAds(firestore) {
  return fetchRegionalFeaturedFeedAds(firestore, 'berkeleyfeature', 'Berkeley feature');
}

/** SF Marina WEEKLY FEATURED carousel (`sfmarinafeature === true`). */
export function fetchSfFeaturedFeedAds(firestore) {
  return fetchRegionalFeaturedFeedAds(firestore, 'sfmarinafeature', 'SF Marina feature');
}

/** SF North Beach WEEKLY FEATURED carousel (`sfnorthbeachfeature === true`). */
export function fetchSfNorthBeachFeaturedFeedAds(firestore) {
  return fetchRegionalFeaturedFeedAds(firestore, 'sfnorthbeachfeature', 'SF North Beach feature');
}

/** SF SOMA WEEKLY FEATURED carousel (`sfsomafeature === true`). */
export function fetchSfSomaFeaturedFeedAds(firestore) {
  return fetchRegionalFeaturedFeedAds(firestore, 'sfsomafeature', 'SF SOMA feature');
}

/** SF Downtown WEEKLY FEATURED carousel (`sfdowntownfeature === true`). */
export function fetchSfDowntownFeaturedFeedAds(firestore) {
  return fetchRegionalFeaturedFeedAds(firestore, 'sfdowntownfeature', 'SF Downtown feature');
}

/**
 * Load collages featured from dinner giveaway opt-ins (`postedInFeed === true`).
 * Admin sets `postedInFeed` on `dinnerGiveawayOptIns` (e.g. Firebase console); collage pixels come from `sharedPosters/{posterId}`.
 *
 * Uses a single-field equality filter (no composite index). Sorts by `bountyAmount` (lowest first), then `createdAt` newest first.
 *
 * @param {import('firebase/firestore').Firestore} firestore
 * @returns {Promise<Array<ReturnType<typeof mapSharedPosterToFeedAd>>>}
 */
export async function fetchDinnerGiveawayPostedFeedAds(firestore) {
  const q = query(
    collection(firestore, 'dinnerGiveawayOptIns'),
    where('postedInFeed', '==', true),
    limit(100),
  );
  let snap;
  try {
    snap = await getDocs(q);
  } catch (err) {
    console.error(
      'dinnerGiveawayOptIns feed query failed (deploy firestore rules; use boolean true for postedInFeed):',
      err,
    );
    return [];
  }

  const sortedDocs = snap.docs
    .slice()
    .sort((a, b) => {
      const bountyDiff = optInBountySortKey(a.data()) - optInBountySortKey(b.data());
      if (bountyDiff !== 0) return bountyDiff;
      return optInCreatedAtMs(b.data()) - optInCreatedAtMs(a.data());
    })
    .slice(0, 50);

  const out = [];
  for (const docSnap of sortedDocs) {
    const o = docSnap.data();
    const rawPid = o.posterId;
    const posterId = typeof rawPid === 'string' ? rawPid.trim() : String(rawPid ?? '').trim();
    if (!posterId) {
      console.warn(
        'Dinner feed: opt-in doc has no posterId (must match sharedPosters doc id). optIn doc id:',
        docSnap.id,
      );
      continue;
    }
    try {
      const ps = await getDoc(doc(firestore, 'sharedPosters', posterId));
      if (!ps.exists()) {
        console.warn(
          'Dinner feed: no sharedPosters for posterId (save collage first so /sharedPosters/{id} exists). posterId=',
          posterId,
          'optIn doc=',
          docSnap.id,
        );
        continue;
      }
      const ad = mapSharedPosterToFeedAd(ps.id, ps.data(), o);
      out.push(await attachAuraColorFromPublicPrimary(firestore, ad));
    } catch (e) {
      console.error('sharedPosters fetch for dinner feed:', posterId, e);
    }
  }
  return out;
}
